<script lang="ts">
    import {getInjector} from 'src/core';
    import {KpLoadableContainer} from 'shared/layouts';
    import type {UUID} from 'src/typings';
    import type {YearMonth} from 'shared/components/kp-calendar/types';
    import type {DayAttendance, SummaryPaymentState, WorkAttendanceOverview} from './types';
    import {AttendanceCalendarService} from './attendance-calendar.service';
    import {onMount} from 'svelte';
    import {DateTime} from 'luxon';
    import {exists} from 'shared/utils/custom-utils';
    import {fade} from 'svelte/transition';
    import AttendanceCalendar from './components/AttendanceCalendar.svelte';
    import AttendanceSummary from './components/summary/AttendanceSummary.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let userRecordId: UUID | null = null;
    export let preloadedData: WorkAttendanceOverview | DayAttendance[] | null = null;
    export let type: 'last-week' | 'month-with-summary';
    export let withoutLoadAnimations = false;
    export let highlightedDate: DateTime | null = null;
    export let yearMonth: YearMonth | null = null;
    export let summaryPaymentStates: SummaryPaymentState[] | null = null;

    if (!exists(userRecordId) && !exists(preloadedData)) {
        throw new Error('Either userRecordId or preloadedData must be provided');
    }

    const service = getInjector().getByClass(AttendanceCalendarService);

    let workAttendance: WorkAttendanceOverview | DayAttendance[];
    let loading = true;
    $: loadError = !loading && !exists(workAttendance);

    onMount(async () => {
        await loadWorkAttendance();
    });

    async function loadWorkAttendance() {
        if (exists(preloadedData)) {
            if (type === 'month-with-summary' && Array.isArray(preloadedData)) {
                throw new Error('Preloaded data is not compatible with month-with-summary type');
            }

            if (type === 'last-week' && !Array.isArray(preloadedData)) {
                throw new Error('Preloaded data is not compatible with last-week type');
            }

            workAttendance = preloadedData;
            loading = false;
            return;
        }

        loading = true;
        const today = DateTime.now();

        if (type === 'last-week') {
            const endDate = (exists(highlightedDate) ? highlightedDate : DateTime.now()).plus({days: 1});
            const oneWeekBefore = endDate.minus({weeks: 1});
            workAttendance = await service.getWorkAttendanceDays(userRecordId, oneWeekBefore.toJSDate(), endDate.toJSDate());
        }

        if (type === 'month-with-summary') {
            const year = exists(yearMonth) ? Number(yearMonth.split('-')[0]) : today.year;
            const month = exists(yearMonth) ? Number(yearMonth.split('-')[1]) : today.month;

            workAttendance = await service.getWorkAttendanceOverview(userRecordId, year, month);
        }

        loading = false;
    }

    function isMonthAttendanceWithSummary(attendance: WorkAttendanceOverview | DayAttendance[]): attendance is WorkAttendanceOverview {
        return 'summary' in attendance && 'days' in attendance;
    }

    $: if (exists(yearMonth) && type === 'month-with-summary') {
        loadWorkAttendance();
    }
</script>

<div class="sutor-attendance-calendar-wrapper">
    <KpLoadableContainer {loading} {loadError} withoutAnimations={withoutLoadAnimations}>
        {#if isMonthAttendanceWithSummary(workAttendance) && exists(workAttendance.warningMessage)}
            <div class="warning-message-container">
                <IconedContent icon="exclamation" iconColor="var(--danger-red)">
                    {workAttendance.warningMessage}
                </IconedContent>
            </div>
        {/if}

        <div class="sutor-attendance-calendar"
             in:fade={{duration: withoutLoadAnimations ? 300 : 0}}
             class:has-warning={isMonthAttendanceWithSummary(workAttendance) && exists(workAttendance.warningMessage)}>

            {#if isMonthAttendanceWithSummary(workAttendance)}
                <AttendanceSummary summary={workAttendance.summary} {summaryPaymentStates}/>
                <AttendanceCalendar attendanceDays={workAttendance.days}/>
            {:else}
                <AttendanceCalendar {highlightedDate} attendanceDays={workAttendance}/>
            {/if}
        </div>
    </KpLoadableContainer>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .sutor-attendance-calendar-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: @spacing-m;

        .warning-message-container {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            position: absolute;
            z-index: 1;
        }

        .sutor-attendance-calendar {
            font-size: @font-size-sm;
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: @spacing-xxl;

            &.has-warning {
                pointer-events: none;
                user-select: none;
                filter: grayscale(100%);
                opacity: 0.5;
            }
        }
    }
</style>